import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Head, Link, router, useForm, usePage } from '@inertiajs/react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import WorkerProfileCard from '@/frontend/components/DigitalWorkers/WorkerProfileCard'
import { AlertTriangle, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { usePermissions } from '@/frontend/hooks/usePermissions'
import type { SharedProps } from '@/frontend/types/inertia'
import OnboardingLaunchPanel from '@/frontend/components/DigitalWorkers/OnboardingLaunchPanel'
import { pickRandomWorkerName } from '@/frontend/pages/Dashboard/DigitalWorkers/nameSuggestions'
import {
  CODEX_BASE_TEMPLATE_ID,
  jobTitleForFleetProfile,
  profileSelectable,
} from '@/frontend/pages/Dashboard/DigitalWorkers/constants'
import EmailProvisioningConfirmationDialog from '@/frontend/pages/Dashboard/DigitalWorkers/new/EmailProvisioningConfirmationDialog'
import ProfileStep from '@/frontend/pages/Dashboard/DigitalWorkers/new/ProfileStep'
import type {
  AvailableIntegration,
  BillingPlanOption,
  DigitalWorkerDetail,
  GoogleAccountOption,
  IntegrationToolkit,
  OrganizationContext,
  OrgIntegration,
} from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import {
  type DigitalWorkerCreateField,
  type DigitalWorkerCreateFormData,
  type FieldErrors,
  type AgentHarness,
} from '@/frontend/pages/Dashboard/DigitalWorkers/new/types'

export interface Props {
  agent_profiles?: import('@/frontend/features/AgentProfiles/types').AgentProfileOption[]
  profile_copy?: import('@/frontend/features/AgentProfiles/types').ProfileCopy
  page_title: string
  current_user_name?: string | null
  current_user_phone?: string | null
  fleet_profiles: string[]
  agent_harnesses: AgentHarness[]
  worker?: DigitalWorkerDetail | null
  integration_categories?: string[]
  all_org_integrations?: OrgIntegration[]
  available_integrations?: AvailableIntegration[]
  integration_toolkits?: IntegrationToolkit[]
  organization_context?: OrganizationContext | null
  google_ads_accounts?: GoogleAccountOption[]
  google_analytics_properties?: GoogleAccountOption[]
  meta_ad_accounts?: GoogleAccountOption[]
  billing_plans: BillingPlanOption[]
}

function validateCreateStep(data: DigitalWorkerCreateFormData): FieldErrors {
  const errors: FieldErrors = {}

  if (!data.name.trim()) errors.name = 'Informe o nome do agente.'
  if (!data.fleet_profile) errors.fleet_profile = 'Escolha um cargo para o colaborador.'
  if (data.fleet_profile && !profileSelectable(data.fleet_profile)) {
    errors.fleet_profile = 'Customer Success não está disponível para novos colaboradores.'
  }

  return errors
}

function StepPanel({ children }: { children: React.ReactNode }) {
  return (
    <section
      data-testid="digital-worker-step-panel"
      data-step={1}
      className="rounded-xl border border-border bg-card p-5 shadow-sm sm:p-6"
    >
      {children}
    </section>
  )
}

const NewDigitalWorker = ({
  page_title,
  current_user_name = null,
  current_user_phone = null,
  fleet_profiles, agent_profiles = [], profile_copy = {},
  agent_harnesses,
  worker = null,
  integration_categories = [],
  all_org_integrations = [],
  available_integrations = [],
  integration_toolkits = [],
  organization_context = null,
  google_ads_accounts = [],
  google_analytics_properties = [],
  meta_ad_accounts = [],
  billing_plans = [],
}: Props) => {
  const { auth } = usePage<SharedProps>().props
  const { can_manage_resources } = usePermissions()
  const isAdmin = auth.user?.is_admin ?? false
  const [localErrors, setLocalErrors] = useState<FieldErrors>({})
  const [confirmActive, setConfirmActive] = useState(false)
  const [emailProvisioningConfirmOpen, setEmailProvisioningConfirmOpen] = useState(false)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollAttemptRef = useRef(0)

  const form = useForm<DigitalWorkerCreateFormData>({
    name: pickRandomWorkerName(),
    job_title: jobTitleForFleetProfile(CODEX_BASE_TEMPLATE_ID),
    fleet_profile: CODEX_BASE_TEMPLATE_ID,
    contact_name: '',
    contact_whatsapp: '',
    personality: '',
    whatsapp_provider: 'none',
    reuse_stationzero_email: isAdmin,
    agent_harness: 'codex',
  })

  const mergedErrors = useMemo<FieldErrors>(() => ({
    ...(form.errors as FieldErrors),
    ...localErrors,
  }), [form.errors, localErrors])

  useEffect(() => {
    const shouldPollWorker = worker && (
      (!worker.provisioning_stale && worker.provisioning_status === 'provisioning') ||
      (!worker.provisioning_stale && worker.provisioning_status === 'pending' && worker.billing?.active) ||
      Boolean(worker.avatar_generating)
    )

    if (!shouldPollWorker) {
      if (pollRef.current) clearTimeout(pollRef.current)
      return
    }

    pollAttemptRef.current = 0
    const poll = () => {
      router.reload({ only: ['worker'] })
      pollAttemptRef.current += 1
      const delay = Math.min(2000 * Math.pow(1.5, pollAttemptRef.current), 10000)
      pollRef.current = setTimeout(poll, delay)
    }

    pollRef.current = setTimeout(poll, 2000)
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [worker])

  useEffect(() => {
    if (!worker || typeof window === 'undefined') return

    const url = new URL(window.location.href)
    if (url.searchParams.get('worker_id') === worker.id) return

    url.searchParams.set('worker_id', worker.id)
    window.history.replaceState({}, '', url.toString())
  }, [worker])

  const handleFieldChange = <K extends DigitalWorkerCreateField>(
    field: K,
    value: DigitalWorkerCreateFormData[K],
  ) => {
    form.setData((previous) => {
      if (field === 'fleet_profile') {
        const profile = String(value)
        return {
          ...previous,
          fleet_profile: profile,
          job_title: agent_profiles.find(row => row.key === profile)?.job_title || jobTitleForFleetProfile(profile),
          personality: '',
        }
      }

      if (field === 'agent_harness') {
        const harness = value as AgentHarness
        const fleetProfile = harness === 'codex' ? CODEX_BASE_TEMPLATE_ID : ''
        return {
          ...previous,
          agent_harness: harness,
          fleet_profile: fleetProfile,
          job_title: fleetProfile ? jobTitleForFleetProfile(fleetProfile) : '',
          personality: '',
        }
      }

      return { ...previous, [field]: value }
    })
    setLocalErrors((previous) => {
      const next = { ...previous }
      delete next[field]
      if (field === 'fleet_profile') delete next.job_title
      if (field === 'agent_harness') {
        delete next.fleet_profile
        delete next.job_title
      }
      if (field === 'name') {
        delete next.stationzero_email
        delete next.instance_name
      }
      return next
    })
    const clearFormErrors = form.clearErrors as (...fields: string[]) => void
    clearFormErrors(
      field,
      ...(field === 'fleet_profile' ? ['job_title'] : []),
      ...(field === 'agent_harness' ? ['fleet_profile', 'job_title'] : []),
      ...(field === 'name' ? ['stationzero_email', 'instance_name'] : []),
    )
  }

  const handleShuffleName = () => {
    handleFieldChange('name', pickRandomWorkerName({ exclude: form.data.name.trim() || undefined }))
  }

  const submitCreateForm = () => {
    form.transform((data) => ({
      name: data.name.trim(),
      fleet_profile: data.fleet_profile,
      job_title: jobTitleForFleetProfile(data.fleet_profile),
      whatsapp_provider: 'none' as const,
      reuse_stationzero_email: isAdmin ? data.reuse_stationzero_email : false,
      agent_harness: data.agent_harness,
    }))

    form.post('/dashboard/digital_workers', {
      preserveScroll: true,
      preserveState: true,
    })
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    const errors = validateCreateStep(form.data)
    setLocalErrors(errors)
    if (Object.keys(errors).length > 0) return

    if (isAdmin && !form.data.reuse_stationzero_email) {
      setEmailProvisioningConfirmOpen(true)
      return
    }

    submitCreateForm()
  }

  const handleConfirmEmailProvisioning = () => {
    setEmailProvisioningConfirmOpen(false)
    submitCreateForm()
  }

  return (
    <>
      <Head title={page_title} />
      <div className={`px-4 ${confirmActive ? 'pt-2 pb-6' : 'py-6'}`}>
        <div>
          {!confirmActive && (
            <>
              <Link href="/dashboard">
                <Button variant="ghost" size="sm" className="-ml-2">
                  <ArrowLeft className="h-4 w-4" />
                  Início
                </Button>
              </Link>

              <div className="py-4">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">{page_title}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Em poucos minutos ele está pronto para começar a trabalhar com você.
                </p>
              </div>
            </>
          )}

          {!can_manage_resources && (
            <Alert variant="warning" className="mb-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Você não tem permissão para criar colaboradores digitais.</AlertTitle>
              <AlertDescription>Peça a um editor ou administrador para concluir esta ação.</AlertDescription>
            </Alert>
          )}

          {worker ? (
            <OnboardingLaunchPanel
              worker={worker}
              integrations={all_org_integrations}
              availableIntegrations={available_integrations}
              integrationToolkits={integration_toolkits}
              integrationCategories={integration_categories}
              organizationContext={organization_context}
              googleAdsAccounts={google_ads_accounts}
              googleAnalyticsProperties={google_analytics_properties}
              metaAdAccounts={meta_ad_accounts}
              canManage={can_manage_resources}
              currentUserName={current_user_name}
              currentUserPhone={current_user_phone}
              billingPlans={billing_plans}
              canGrantAdminSubscription={isAdmin}
              onConfirmStateChange={setConfirmActive}
            />
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
              <StepPanel>
                <div className="space-y-5">
                  <ProfileStep
                    data={form.data}
                    errors={mergedErrors}
                    disabled={form.processing || !can_manage_resources}
                    fleetProfiles={fleet_profiles}
                    profileOptions={agent_profiles} profileCopy={profile_copy}
                    onFieldChange={handleFieldChange}
                    onShuffleName={handleShuffleName}
                    showEmailProvisioningToggle={isAdmin}
                    showHarnessSelector
                    showCodexTemplates={isAdmin}
                    agentHarnesses={agent_harnesses}
                  />

                  <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-end">
                    <Button
                      type="submit"
                      disabled={form.processing || !can_manage_resources}
                      className="group transition-all hover:-translate-y-[1px] hover:shadow-lg hover:shadow-violet-500/20"
                    >
                      {form.processing && <Loader2 className="h-4 w-4 animate-spin" />}
                      Continuar
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Button>
                  </div>
                </div>
              </StepPanel>

              <aside className="lg:sticky lg:top-6 lg:self-start" data-testid="worker-profile-aside">
                <WorkerProfileCard
                  name={form.data.name}
                  fleetProfile={form.data.fleet_profile}
                  jobTitle={form.data.job_title}
                  avatarUrl={null}
                  contactName={form.data.contact_name}
                  personality={form.data.personality}
                />
              </aside>
            </form>
          )}
          {!worker && (
            <EmailProvisioningConfirmationDialog
              open={emailProvisioningConfirmOpen}
              processing={form.processing}
              onOpenChange={setEmailProvisioningConfirmOpen}
              onConfirm={handleConfirmEmailProvisioning}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default NewDigitalWorker
